import { useState, useCallback, useEffect } from 'react'
import { twinFetchPostJSON } from '../../globals/data'
import { TwinDictionary } from '../../globals/dictionary'
import { getAllWorkingCenters, getUserPermissions } from '../../reducers/getters'
import { permissionCheck } from '../../../baseComponents/PermissionChecker/function'

interface GetDictPaymentBanksProps {
    WorkingCenterId: number
    CustomerId?: number
}

const useGetDictPaymentBanks = ({ CustomerId, WorkingCenterId }: GetDictPaymentBanksProps) => {
    const [dictPaymentBanks, setDictPaymentBanks] = useState<DictPaymentBanksTy | null>(null)
    const getDataBanks = useCallback(async () => {
        let customerBanks = []
        let workingCenterBanks = []
        if (CustomerId && permissionCheck(getUserPermissions('customer.profile.bank'), 'read')) {
            customerBanks = await twinFetchPostJSON('/api/app/customer/customerBank/getAllCustomerBanks', { where: { CustomerId } })
        }
        if (WorkingCenterId && permissionCheck(getUserPermissions('workingCenter.bank'), 'read')) {
            workingCenterBanks = await twinFetchPostJSON('/api/app/workingCenter/workingCenterBank/getAllWorkingCenterBanks', { where: { WorkingCenterId } })
        }
        setDictPaymentBanks({
            customerBanks: customerBanks.length ? getDictBanksAndDefault(customerBanks) : { defaultId: 0, items: {} },
            workingCenterBanks: workingCenterBanks ? getDictBanksAndDefault(workingCenterBanks) : { defaultId: 0, items: {} },
            workingCenterShift4Devices: {items: {}, defaultId: 0}
        })
    }, [setDictPaymentBanks, CustomerId, WorkingCenterId])

    useEffect(() => {
        getDataBanks()
    }, [getDataBanks])

    return { dictPaymentBanks }
}

interface GetDictPaymentBanksTPVProps {
    WorkingCenterId: number
    WorkingCenterStationId: number
    CustomerId?: number
}

export const useGetDictPaymentBanksTPV = ({ CustomerId, WorkingCenterId, WorkingCenterStationId }: GetDictPaymentBanksTPVProps) => {
    const [dictPaymentBanks, setDictPaymentBanks] = useState<DictPaymentBanksTy | null>(null)
    const getDataBanks = useCallback(async () => {
        let customerBanks = []
        if (CustomerId && permissionCheck(getUserPermissions('customer.profile.bank'), 'read')) {
            customerBanks = await twinFetchPostJSON('/api/app/customer/customerBank/getAllCustomerBanks', { where: { CustomerId } })
        }
        const myWorkingCenter = getAllWorkingCenters()?.[WorkingCenterId]
        setDictPaymentBanks({
            customerBanks: customerBanks.length ? getDictBanksAndDefault(customerBanks) : { defaultId: 0, items: {} },
            workingCenterBanks: myWorkingCenter?.dictBanks ? { items: myWorkingCenter.dictBanks, defaultId: myWorkingCenter.bankDefault || 0 } : { defaultId: 0, items: {} },
            workingCenterShift4Devices: myWorkingCenter?.WorkingCenterShift4Devices ? getDictShift4DevicesAndDefault(myWorkingCenter?.WorkingCenterShift4Devices, WorkingCenterStationId) : { defaultId: 0, items: {} },
        })
    }, [setDictPaymentBanks, CustomerId, WorkingCenterId, WorkingCenterStationId])

    useEffect(() => {
        getDataBanks()
    }, [getDataBanks])

    return { dictPaymentBanks }
}

type DictBankAndDefaultTy = { defaultId: number, items: TwinDictionary }
export type DictPaymentBanksTy = {
    customerBanks: DictBankAndDefaultTy
    workingCenterBanks: DictBankAndDefaultTy
    workingCenterShift4Devices: DictBankAndDefaultTy
}

const getDictBanksAndDefault = (arrayBanks: TwinDictionary[]) => {
    const result: DictBankAndDefaultTy = { defaultId: 0, items: {} }
    for (const element of arrayBanks) {
        result.items[element.id] = { ...element, fullname: element.name + '-' + element.iban }
        if (element.defaultBank) {
            result.defaultId = element.id
        }
    }
    return result
}

const getDictShift4DevicesAndDefault = (shift4Devices: TwinDictionary[], WorkingCenterStationId: number | null) => {
    const result: DictBankAndDefaultTy = { defaultId: 0, items: {} }
    for (const element of shift4Devices) {
        if (WorkingCenterStationId === element.WorkingCenterStationId){
            result.items[element.id] = element
            if(!result.defaultId){
                result.defaultId = element.id
            }
        }
    }
    return result
}

export default useGetDictPaymentBanks